<template>
  <modal
    name="modal--product-transfer"
    transition="nice-modal-fade"
    :click-to-close="false"
    :width="350"
    :height="'auto'"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    class="py-10"
  >
    <div
      class="flex flex-col text-black h-full bg-white overflow-hidden w-full justify-between p-4"
    >
      <div class="w-full">
        <div class="text-center text-4xl mb-1 font-extralight tracking-tight">
          {{ editMode ? 'Transfer' : 'Transfer Product' }}
        </div>
        <div class="mt-4">
          <div class="bg-gray-200 rounded-t-lg p-1 flex">
            <button
              class="flex-1 px-3 py-2 rounded-lg focus:outline-none transition-colors duration-200 text-sm"
              :class="[
                activeTab === 'recipe'
                  ? 'bg-white text-gray-800 font-bold shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              ]"
              @click="activeTab = 'recipe'"
            >
              Recipe
            </button>
            <button
              class="flex-1 px-3 py-2 rounded-lg focus:outline-none transition-colors duration-200 text-sm"
              :class="[
                activeTab === 'product'
                  ? 'bg-white text-gray-800 font-bold shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              ]"
              @click="activeTab = 'product'"
            >
              Product
            </button>
          </div>

          <div
            class="bg-white border border-gray-200 rounded-b-lg p-3 shadow-sm"
          >
            <div v-if="activeTab === 'recipe'">
              <label
                class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
              >
                Select Recipe
              </label>
              <select
                v-model="recipeId"
                class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              >
                <option value="" disabled>Select a recipe</option>
                <option v-for="id in sortedRecipeIds" :key="id" :value="id">
                  {{ recipeName(id) }}
                </option>
              </select>
              <div class="mt-2 text-sm text-gray-600">
                <div class="flex justify-between">
                  <div>
                    <span class="font-semibold">Unit Type:</span>
                    {{ activeTab === 'recipe' ? selectedRecipeUnit : '' }}
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm font-medium text-gray-700"
                      >Unit Cost:</span
                    >
                    <span class="text-base">
                      {{ recipeId ? `$${selectedUnitCost.toFixed(2)}` : '' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="activeTab === 'product'">
              <label
                class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
              >
                Select Product
              </label>
              <select
                v-model="selectedId"
                class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              >
                <option value="" disabled>Select a product</option>
                <option v-for="id in sortedProductIds" :key="id" :value="id">
                  {{ productName(id) }}
                </option>
              </select>
              <div class="mt-2 text-sm text-gray-600">
                <div class="flex justify-between">
                  <div>
                    <span class="font-semibold">Unit Type:</span>
                    {{ activeTab === 'product' ? selectedProductUnit : '' }}
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm font-medium text-gray-700"
                      >Unit Cost:</span
                    >
                    <span class="text-base">
                      {{ selectedId ? `$${selectedUnitCost.toFixed(2)}` : '' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4">
          <label
            class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          >
            Quantity
          </label>

          <!-- Weight input (oz-wt) -->
          <div v-if="showWeightInput" class="flex flex-row space-x-2 mt-2">
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Pounds</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                v-model.number="pounds"
                autocomplete="off"
              />
            </div>
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Ounces</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                max="15.99"
                step="0.01"
                v-model.number="ounces"
                autocomplete="off"
              />
            </div>
          </div>

          <!-- Volume input (oz-fl) -->
          <div v-else-if="showVolumeInput" class="flex flex-row space-x-2 mt-2">
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Gallons</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                v-model.number="gallons"
                autocomplete="off"
              />
            </div>
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Quarts</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                max="3"
                v-model.number="quarts"
                autocomplete="off"
              />
            </div>
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Ounces</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                max="31.99"
                step="0.01"
                v-model.number="fluidOunces"
                autocomplete="off"
              />
            </div>
          </div>

          <!-- Standard quantity input for other unit types -->
          <input
            v-else
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="number"
            min="1"
            placeholder="Enter quantity"
            v-model.number="rawQuantity"
            autocomplete="off"
          />
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="sourceStore"
        >
          Source Store (optional)
        </label>
        <div class="mt-2">
          <select
            class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
            id="sourceStore"
            v-model="sourceStore"
            @click.stop
          >
            <option value="">Any</option>
            <option
              v-for="storeId in sourceStoreOptions"
              :key="storeId"
              :value="storeId"
            >
              {{ storeId ? getStoreName(storeId) : '' }}
            </option>
          </select>
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="destinationStore"
        >
          Destination Store
        </label>
        <div class="mt-2">
          <select
            class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
            id="destinationStore"
            v-model="destinationStore"
            :disabled="!canSelectAnyStore"
            @click.stop
          >
            <option
              v-for="storeId in destinationStoreOptions"
              :key="storeId"
              :value="storeId"
            >
              {{ getStoreName(storeId) }}
            </option>
          </select>
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Date
          <v-date-picker
            v-model="date"
            :is-required="true"
            :popover="{
              placement: $screens({ default: 'bottom-end', sm: 'bottom' }),
              positionFixed: true,
              visibility: 'click'
            }"
          >
            <template #default="{ inputValue, inputEvents }">
              <input
                class="text-base font-normal mt-2 py-3 px-4 focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="text"
                :value="inputValue"
                v-on="inputEvents"
                readonly
              />
            </template>
          </v-date-picker>
        </label>

        <div class="mt-6 flex flex-row justify-between">
          <button
            class="buttonOutline focus:shadow-outline focus:outline-none"
            type="button"
            @click="$modal.hide('modal--product-transfer')"
          >
            Cancel
          </button>
          <button
            class="ml-3 button focus:shadow-outline focus:outline-none"
            type="button"
            :disabled="!isValid"
            @click="submit"
          >
            {{ editMode ? 'Update' : 'Request' }}
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import VDatePicker from 'v-calendar/lib/components/date-picker.umd'

export default {
  name: 'ModalProductTransfer',
  components: {
    VDatePicker
  },
  data() {
    return {
      editMode: false,
      editId: null,
      sourceStore: '',
      destinationStore: '',
      recipeId: '',
      productId: null,
      rawQuantity: 1, // Use rawQuantity for the raw value
      notes: '',
      activeTab: 'recipe',
      date: new Date(),
      selectedProduct: '',
      pounds: 0,
      ounces: 0,
      gallons: 0,
      quarts: 0,
      fluidOunces: 0
    }
  },
  computed: {
    ...mapGetters('user', [
      'isManager',
      'isAdmin',
      'isDeveloper',
      'stores',
      'storeName'
    ]),
    ...mapGetters('storeModule/transfers', ['transferById']),
    ...mapGetters('recipes', [
      'recipes',
      'isRecipe',
      'recipeName',
      'recipeById'
    ]),
    ...mapGetters('global', [
      'products',
      'productById',
      'productName',
      'isProduct',
      'storeRegionById',
      'storesByRegionId',
      'settingsStoreIds'
    ]),
    ...mapGetters('storeModule', [
      'productPriceByProductId',
      'recipeCostById',
      'localProductIdFromGenericId',
      'localRecipeIdFromGenericId'
    ]),

    // Get the current store's region
    currentStoreRegion() {
      return this.storeRegionById(this.storeName) || ''
    },

    // Get all stores in the same region as the current store
    storesInSameRegion() {
      if (!this.currentStoreRegion) return []
      return this.storesByRegionId(this.currentStoreRegion) || []
    },

    selectedId: {
      get() {
        return this.activeTab === 'recipe' ? this.recipeId : this.productId
      },
      set(value) {
        if (this.activeTab === 'recipe') {
          this.recipeId = value
        } else {
          this.productId = value
        }
      }
    },
    selectedProductUnit() {
      if (this.activeTab === 'product') {
        const product = this.productById(this.productId)
        return product ? product.unitCounting : ''
      }
      return ''
    },
    selectedRecipeUnit() {
      if (this.activeTab === 'recipe') {
        const recipe = this.recipeById(this.recipeId)
        return recipe ? recipe.unitType : ''
      }
      return ''
    },
    selectedUofM() {
      if (this.activeTab === 'recipe') {
        const recipe = this.recipeById(this.recipeId)
        return recipe ? recipe.unitType : ''
      } else {
        const product = this.productById(this.productId)
        return product ? product.unitCounting : ''
      }
    },
    storeOptions() {
      return [this.storeName]
    },
    destinationStoreOptions() {
      if (this.canSelectAnyStore) {
        return this.getSortedStores()
      }
      // For managers, only their store
      return [this.storeName]
    },
    sourceStoreOptions() {
      if (this.canSelectAnyStore) {
        // Admins/devs can select any store
        return ['', ...this.getSortedStores()]
      }

      // For regular users, only show stores in the same region
      // Filter out the current store and sort them
      const regionStores = this.storesInSameRegion
        .filter((id) => id !== this.storeName)
        .sort((a, b) => {
          return this.getStoreName(a)
            .toLowerCase()
            .localeCompare(this.getStoreName(b).toLowerCase())
        })

      return ['', ...regionStores]
    },
    isValid() {
      return (
        ((this.activeTab === 'recipe' && this.recipeId) ||
          (this.activeTab === 'product' && this.productId)) &&
        this.quantity > 0 &&
        this.destinationStore
      )
    },
    selectedUnitCost() {
      if (!this.selectedId) return 0

      if (this.activeTab === 'recipe') {
        return this.recipeCostById(this.recipeId) || 0
      } else {
        // For products, we need to get the cost from the source store if it exists
        if (this.sourceStore) {
          const localProductId = this.localProductIdFromGenericId(
            this.productId,
            this.sourceStore
          )
          const productPrice = this.productPriceByProductId(localProductId)
          return productPrice ? productPrice.pricePerCountingUnit : 0
        }
        // If no source store, try destination store
        const localProductId = this.localProductIdFromGenericId(
          this.productId,
          this.destinationStore
        )
        const productPrice = this.productPriceByProductId(localProductId)
        return productPrice ? productPrice.pricePerCountingUnit : 0
      }
    },
    canSelectAnyStore() {
      return false //this.isAdmin || this.isDeveloper
    },
    sortedRecipeIds() {
      if (!this.recipes) return []
      return Object.keys(this.recipes)
        .filter((id) => {
          // Consider items transferable if the property is missing, null, or true
          return this.recipes[id].transferable !== false
        })
        .sort((a, b) => {
          const nameA = (this.recipeName(a) || '').toLowerCase()
          const nameB = (this.recipeName(b) || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
    },
    sortedProductIds() {
      if (!this.products) return []

      return Object.keys(this.products)
        .filter((id) => {
          // Get the generic product ID
          const genericId = this.products[id]
          if (!genericId) return false

          // Check if the generic product is transferable
          const genericProduct = this.productById(genericId)
          if (!genericProduct) return true // Default to true if generic product not found

          // Consider transferable if property doesn't exist or is true
          return genericProduct.transferable !== false
        })
        .sort((a, b) => {
          const nameA = (this.productName(a) || '').toLowerCase()
          const nameB = (this.productName(b) || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
    },
    showWeightInput() {
      if (this.activeTab === 'recipe' && this.recipeId) {
        const recipe = this.recipeById(this.recipeId)
        return recipe && recipe.unitType === 'oz-wt'
      }
      return false
    },

    showVolumeInput() {
      if (this.activeTab === 'recipe' && this.recipeId) {
        const recipe = this.recipeById(this.recipeId)
        return recipe && recipe.unitType === 'oz-fl'
      }
      return false
    },

    // Fix the quantity computed property
    quantity() {
      console.log('Computing quantity value')
      if (this.showWeightInput) {
        const total = (this.pounds || 0) * 16 + parseFloat(this.ounces || 0)
        console.log(
          `Weight calculation: ${this.pounds} pounds and ${this.ounces} ounces = ${total} total ounces`
        )
        return total
      } else if (this.showVolumeInput) {
        const total =
          (this.gallons || 0) * 128 +
          (this.quarts || 0) * 32 +
          parseFloat(this.fluidOunces || 0)
        console.log(
          `Volume calculation: ${this.gallons} gallons, ${this.quarts} quarts, and ${this.fluidOunces} fluid ounces = ${total} total fluid ounces`
        )
        return total
      } else {
        console.log(`Standard quantity: ${this.rawQuantity}`)
        return this.rawQuantity || 1
      }
    }
  },
  methods: {
    ...mapActions('config', ['setFabVisible']),
    ...mapActions('storeModule/transfers', [
      'createTransfer',
      'updateTransfer'
    ]),
    hideModal() {
      this.$modal.hide('modal--product-transfer')
    },
    getSortedStores() {
      return this.stores.sort((a, b) => {
        return this.getStoreName(a)
          .toLowerCase()
          .localeCompare(this.getStoreName(b).toLowerCase())
      })
    },
    beforeOpen(event) {
      this.setFabVisible(false)
      if (event.params && event.params.transferId) {
        // Edit mode
        this.editMode = true
        this.editId = event.params.transferId
        const transfer = this.transferById(event.params.transferId)

        if (!transfer) {
          console.error(
            'Could not find transfer with ID:',
            event.params.transferId
          )
          return
        }

        // First set the active tab based on item type
        this.activeTab = transfer.itemType || 'recipe'

        // Then set the appropriate ID based on the item type
        if (transfer.itemType === 'recipe') {
          this.recipeId = transfer.itemId
          this.productId = '' // Clear product selection

          // Set quantity after recipeId to ensure computed properties are updated
          this.rawQuantity = transfer.quantity || 1

          // Convert ounces to compound units if needed
          if (this.showWeightInput) {
            this.pounds = Math.floor(transfer.quantity / 16)
            this.ounces = parseFloat((transfer.quantity % 16).toFixed(2))
            console.log(
              `Editing transfer: converted ${transfer.quantity} oz to ${this.pounds} pounds and ${this.ounces} ounces`
            )
          } else if (this.showVolumeInput) {
            this.gallons = Math.floor(transfer.quantity / 128)
            const remainingOz = transfer.quantity % 128
            this.quarts = Math.floor(remainingOz / 32)
            this.fluidOunces = parseFloat((remainingOz % 32).toFixed(2))
            console.log(
              `Editing transfer: converted ${transfer.quantity} fl oz to ${this.gallons} gallons, ${this.quarts} quarts, and ${this.fluidOunces} fluid ounces`
            )
          }
        } else {
          this.productId = transfer.itemId
          this.recipeId = '' // Clear recipe selection
          this.rawQuantity = transfer.quantity || 1
        }

        // Populate the rest of the form with explicit null checks
        this.notes = transfer.notes || ''
        this.sourceStore = transfer.sourceStoreId || ''
        this.destinationStore = transfer.destinationStoreId

        // Handle date conversion with explicit null check
        this.date =
          transfer.date && transfer.date.seconds
            ? new Date(transfer.date.seconds * 1000)
            : new Date()
      } else {
        // Create mode
        this.editMode = false
        this.editId = null
        this.resetForm()
      }
    },
    beforeClose() {
      this.setFabVisible(true)
      this.editMode = false
      this.editId = null
      this.resetForm()
    },
    resetForm() {
      this.activeTab = 'recipe'
      this.recipeId = ''
      this.productId = ''
      this.rawQuantity = 1
      this.pounds = 0
      this.ounces = 0
      this.gallons = 0
      this.quarts = 0
      this.fluidOunces = 0
      this.notes = ''
      this.sourceStore = '' // Start with blank source store
      this.destinationStore = this.storeName // Default to current store
      this.date = new Date()
    },
    submit() {
      if (!this.isValid) return

      // Calculate the final quantity value based on unit type
      let finalQuantity = this.quantity
      console.log(`Submitting with final quantity: ${finalQuantity}`)

      const payload = {
        itemType: this.activeTab,
        itemId: this.activeTab === 'recipe' ? this.recipeId : this.productId,
        itemName:
          this.activeTab === 'recipe'
            ? this.recipeName(this.recipeId)
            : this.productName(this.productId),
        quantity: finalQuantity,
        unitType: this.selectedUofM,
        notes: this.notes,
        sourceStoreId: this.sourceStore || null,
        sourceStoreName: this.sourceStore
          ? this.getStoreName(this.sourceStore)
          : '',
        destinationStoreId: this.destinationStore,
        destinationStoreName: this.getStoreName(this.destinationStore),
        date: this.date,
        unitCost: this.selectedUnitCost,
        totalCost: this.selectedUnitCost * finalQuantity,
        // Always include these fields with default empty strings
        sourceItemId: '',
        destinationItemId: ''
      }

      console.log('Transfer payload:', payload)

      if (this.editMode) {
        this.updateTransfer({ id: this.editId, ...payload })
          .then(() => {
            this.$modal.hide('modal--product-transfer')
          })
          .catch((error) => {
            console.error('Error updating transfer:', error)
          })
      } else {
        this.createTransfer(payload)
          .then(() => {
            this.$modal.hide('modal--product-transfer')
          })
          .catch((error) => {
            console.error('Error creating transfer:', error)
          })
      }
    },
    getStoreName(id) {
      return id && this.settingsStoreIds[id] ? this.settingsStoreIds[id].name : ''
    }
  },
  watch: {
    // Watch for recipe changes to update unit display
    recipeId() {
      // Reset compound values when recipe changes
      this.pounds = 0
      this.ounces = 0
      this.gallons = 0
      this.quarts = 0
      this.fluidOunces = 0
      this.rawQuantity = 1
    },

    activeTab() {
      // Reset compound values when tab changes
      this.pounds = 0
      this.ounces = 0
      this.gallons = 0
      this.quarts = 0
      this.fluidOunces = 0
      this.rawQuantity = 1
    },
    // Add watchers for the compound unit inputs to help with debugging
    pounds() {
      console.log(
        `Pounds changed to ${this.pounds}, total ounces now: ${this.quantity}`
      )
    },
    ounces() {
      console.log(
        `Ounces changed to ${this.ounces}, total ounces now: ${this.quantity}`
      )
    },
    gallons() {
      console.log(
        `Gallons changed to ${this.gallons}, total fluid ounces now: ${this.quantity}`
      )
    },
    quarts() {
      console.log(
        `Quarts changed to ${this.quarts}, total fluid ounces now: ${this.quantity}`
      )
    },
    fluidOunces() {
      console.log(
        `Fluid ounces changed to ${this.fluidOunces}, total fluid ounces now: ${this.quantity}`
      )
    }
  }
}
</script>
